import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CrearEmpleadoFacade } from '../services/crear-empleado.facade';
import { DatosPersonaCrearEmpleadoComponent } from '../components/datos-persona-crear/datos-persona-crear.component';
import { InformacionAdicionalCrearEmpleadoComponent } from '../components/informacion-adicional-crear/informacion-adicional-crear.component';
import { MovilidadConsultorComponent } from 'src/app/presentation/empleados/modificar-empleado/components/movilidad-consultor/movilidad-consultor.component';
import { DatosConsultorEmpleadoComponent } from 'src/app/presentation/empleados/modificar-empleado/components/datos-consultor/datos-consultor.component';
import { PersonaDialogComponent } from 'src/app/presentation/personas/persona-dialog.component';
import { MunicipioSeleccionado } from 'src/app/presentation/common/busquedas/busqueda-municipio-dialog.component';
import { PersonaSeleccionada } from 'src/app/presentation/common/busquedas/buscador-persona-dialog.component';
import { EmpleadoAdminResponse } from 'src/app/domain/model/empleado/empleado';

/**
 * Contenido de "Crear Empleado/Consultor" (HU1259115), embebido en un
 * `p-dialog` desde `AdministrarEmpleadosComponent` — mismo patrón de modal
 * que `ModificarEmpleadoComponent` (fase 19): sin ruta propia, se abre/cierra
 * desde el listado y emite `guardado`/`cancelado`.
 *
 * <p>Legacy: `CrearEmpleadoViewImpl`. Reutiliza `MovilidadConsultorComponent`
 * y `DatosConsultorEmpleadoComponent` de `modificar-empleado` sin ningún
 * `@Input() modo` — ambos operan puramente sobre `form: EmpleadoRequest` sin
 * ninguna referencia a un empleado ya existente, así que son 100%
 * reutilizables tal cual. El panel "Información Adicional"
 * (Salario/Nivel de Estudio/Cargo/Fechas de contrato) usa un componente
 * nuevo, `InformacionAdicionalCrearEmpleadoComponent` (fase 25/26) — NO
 * reutiliza `InformacionAdicionalEmpleadoComponent` de Modificar (100% de
 * solo lectura) porque en Crear es condicionalmente editable campo por
 * campo. Fuente real: `MPSC_DATOS_NOMINA` (tabla externa a
 * `TPSC_EMPLEADOS`), consultada por `cdpersona` al seleccionar la persona —
 * ver `CrearEmpleadoFacade.cargarDatosNomina()`. "Datos de
 * Persona" usa un dumb component nuevo (`DatosPersonaCrearEmpleadoComponent`)
 * en vez de `DatosPersonaEmpleadoComponent` — ver Javadoc de ese componente
 * para la justificación completa de por qué no se pudo reutilizar
 * limpiamente con un `@Input() modo`. Incluye "Ver detalle Persona" (fase 25
 * — SÍ existe en el legado de Crear, contra lo que afirmaba HU1259115.md
 * antes de esta auditoría) reutilizando `PersonaDialogComponent` igual que
 * Modificar.</p>
 */
@Component({
  selector: 'app-crear-empleado',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ProgressSpinnerModule,
    DatosPersonaCrearEmpleadoComponent,
    InformacionAdicionalCrearEmpleadoComponent,
    MovilidadConsultorComponent,
    DatosConsultorEmpleadoComponent,
    PersonaDialogComponent,
  ],
  providers: [CrearEmpleadoFacade],
  template: `
    <div class="p-3 sm:p-4 overflow-x-hidden">
      @if (facade.cargandoCatalogos()) {
        <div class="flex justify-center p-8">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>
      }

      @if (!facade.cargandoCatalogos()) {
        @if (facade.mensaje()) {
          <div class="p-3 mb-4 rounded"
            [ngClass]="{
              'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200': facade.mensajeTipo() === 'warning',
              'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200': facade.mensajeTipo() === 'error',
              'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200': facade.mensajeTipo() === 'info'
            }">
            {{ facade.mensaje() }}
          </div>
        }

        <app-datos-persona-crear-empleado
          [form]="facade.form"
          [personaSeleccionada]="facade.personaSeleccionada()"
          [esAsesor]="facade.esAsesor()"
          [mostrarProgramable]="facade.mostrarProgramable()"
          [oficinas]="facade.oficinas()"
          [tiposEmpleado]="facade.tiposEmpleado()"
          [perfiles]="facade.perfiles()"
          [tiposAsesorReal]="facade.tiposAsesorReal()"
          [tiposFacturacionFiltradas]="facade.tiposFacturacionFiltradas()"
          [tipoFacturacionEditable]="facade.tipoFacturacionEditable()"
          [proyectos]="facade.proyectos()"
          [soluciones]="facade.soluciones()"
          [municipioResidenciaDisplay]="facade.municipioResidenciaDisplay()"
          [municipioPrestacionDisplay]="facade.municipioPrestacionDisplay()"
          [cargandoPersonaDetalle]="facade.cargandoPersonaDetalle()"
          (personaSeleccionadaEvento)="onPersonaSeleccionada($event)"
          (personaLimpiada)="facade.limpiarPersona()"
          (cambioTipoEmpleado)="facade.onCambioTipoEmpleado()"
          (cambioTipoAsesorReal)="facade.onCambioTipoAsesorReal()"
          (cambioTipoFacturacion)="facade.onCambioTipoFacturacion()"
          (verDetallePersona)="onVerDetallePersona()"
          (municipioResidenciaSeleccionado)="onMunicipioResidenciaSeleccionado($event)"
          (municipioResidenciaLimpiado)="facade.limpiarMunicipioResidencia()"
          (municipioPrestacionSeleccionado)="onMunicipioPrestacionSeleccionado($event)"
          (municipioPrestacionLimpiado)="facade.limpiarMunicipioPrestacion()">
        </app-datos-persona-crear-empleado>

        <app-informacion-adicional-crear-empleado
          [salarioDisplay]="facade.salarioDisplay()"
          [nivelEstudio]="facade.form.dsnivelEstudio ?? null"
          [cargo]="facade.form.dscargo ?? null"
          [feinicioContrato]="facade.form.feinicioContrato ?? null"
          [fefinContrato]="facade.form.fefinContrato ?? null">
        </app-informacion-adicional-crear-empleado>

        @if (facade.esAsesor()) {
          <app-movilidad-consultor
            [form]="facade.form"
            [opcionesTipoAsignacion]="facade.opcionesTipoAsignacion">
          </app-movilidad-consultor>

          <app-datos-consultor-empleado
            [form]="facade.form"
            [gruposConocimiento]="facade.gruposConocimiento()"
            (cambioAdscrito)="facade.onCambioAdscrito()"
            (cambioEspecializacionHigiene)="facade.onCambioEspecializacionHigiene()">
          </app-datos-consultor-empleado>
        }

        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button label="Cancelar" icon="pi pi-times" [outlined]="true"
            styleClass="min-h-[44px]" (onClick)="onCancelar()"></p-button>
          <p-button label="Grabar" icon="pi pi-check" styleClass="min-h-[44px]"
            [loading]="facade.guardando()" (onClick)="grabar()"></p-button>
        </div>
      }
    </div>

    @if (mostrarDialogoDetallePersona()) {
      <app-persona-dialog
        [visible]="mostrarDialogoDetallePersona()"
        (visibleChange)="mostrarDialogoDetallePersona.set($event)"
        [editMode]="true"
        [soloLectura]="true"
        [persona]="facade.personaDetalle()"
        [tiposEmpresa]="facade.tiposEmpresaPersona()"
        [tamaniosEmpresa]="facade.tamaniosEmpresaPersona()">
      </app-persona-dialog>
    }
  `
})
export class CrearEmpleadoComponent implements OnChanges {
  protected readonly facade = inject(CrearEmpleadoFacade);

  /** Se incrementa cada vez que el padre reabre el modal — dispara la recarga/reinicio del formulario. */
  @Input() abrir: number | null = null;

  /** Se emite tras guardar con éxito con la respuesta mínima del backend — el padre cierra el diálogo y refresca el listado. */
  @Output() guardado = new EventEmitter<EmpleadoAdminResponse>();

  /** Se emite al cancelar — el padre solo cierra el diálogo. */
  @Output() cancelado = new EventEmitter<void>();

  /** Diálogo de solo lectura de "Ver Detalle Persona" (legacy: `activity.verDetallePersona()`, auditoría fase 25). */
  protected readonly mostrarDialogoDetallePersona = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['abrir'] && this.abrir != null) {
      this.facade.reiniciar();
      this.facade.cargarCatalogos();
    }
  }

  onPersonaSeleccionada(persona: PersonaSeleccionada): void {
    this.facade.seleccionarPersona(persona);
  }

  /**
   * Legacy: `activity.verDetallePersona()` — carga el detalle completo de
   * la persona seleccionada (fetch aparte, ver Javadoc de
   * `CrearEmpleadoFacade.cargarDetallePersona()`) y solo entonces abre el
   * diálogo, para no mostrarlo vacío mientras carga.
   */
  async onVerDetallePersona(): Promise<void> {
    await this.facade.cargarDetallePersona();
    this.mostrarDialogoDetallePersona.set(true);
  }

  onMunicipioResidenciaSeleccionado(municipio: MunicipioSeleccionado): void {
    this.facade.seleccionarMunicipioResidencia(municipio.cdmunicipio, municipio.dsmunicipio);
  }

  onMunicipioPrestacionSeleccionado(municipio: MunicipioSeleccionado): void {
    this.facade.seleccionarMunicipioPrestacion(municipio.cdmunicipio, municipio.dsmunicipio);
  }

  async grabar(): Promise<void> {
    const creado = await this.facade.guardar();
    if (creado) {
      this.guardado.emit(creado);
    }
  }

  onCancelar(): void {
    this.cancelado.emit();
  }
}
